import type { CSSProperties } from 'react';
import isUndefined from 'lodash/isUndefined';

// Matches various forms of empty paragraph tags at the end of a string:
// 1. <p>   </p> (with optional whitespace/&nbsp;)
// 2. <p /> (self-closing)
// 3. <p><br/></p> (with br having optional ProseMirror class)
// 4. <p><br><br>...</p> (with multiple <br> tags and optional last br with ProseMirror class)
// 5. Can match multiple consecutive empty paragraphs
const EMPTY_P_TAG_END_REGEX =
  /(?<temp1><p[^>]*>(?:(?:\s|&nbsp;|<br\s*\/?>|<br\s+class="ProseMirror-trailingBreak"\s*\/?>)*|)<\/p>\s*|<p\s*\/>\s*|<p><br\s*\/?>*(?:<br\s+class="ProseMirror-trailingBreak"\s*\/?>)*<\/p>\s*)+$/;
export function removeEmptyTrailingPTags(html: string) {
  const cleanHtml = html.replace(EMPTY_P_TAG_END_REGEX, '').trim();

  return cleanHtml || undefined;
}

const DEFAULT_LINE_HEIGHT = '1.25rem';
export const DEFAULT_MIN_NUMBER_OF_LINES = 1;

/**
 *
 * @param minNumberOfLines - minimum number of lines of richtext editor to show always
 * @param maxNumberOfLines - maximum number of lines of richtext editor to show upfront and then show scroll
 * @returns sets CSS variable for min and max number of lines
 */
export const getContainerStyles = (
  minNumberOfLines: number | undefined,
  maxNumberOfLines: number | undefined,
): CSSProperties => {
  return {
    '--min-number-of-lines': minNumberOfLines ? `${minNumberOfLines}` : DEFAULT_MIN_NUMBER_OF_LINES,
    ...(!isUndefined(maxNumberOfLines) && { '--max-number-of-lines': `${maxNumberOfLines}` }),
  } as CSSProperties;
};

/**
 *
 * @param minNumberOfLines - minimum number of lines of richtext editor to show always
 * @param maxNumberOfLines - maximum number of lines of richtext editor to show upfront and then show scroll
 * @returns minHeight, overflowY and maxHeight of richtext editor
 */
export const getEditorContentStyles = (
  maxNumberOfLines: number | undefined,
  styleState: Record<keyof CSSStyleDeclaration, string> | undefined,
): CSSProperties => {
  const paddingTop = styleState
    ? parseInt(styleState['padding-top' as keyof CSSStyleDeclaration], 10)
    : 0;
  const paddingBottom = styleState
    ? parseInt(styleState['padding-bottom' as keyof CSSStyleDeclaration], 10)
    : 0;

  const totalVerticalPadding = paddingTop + paddingBottom;

  /**
   * Editor when used in MessageInput also receives minHeight and maxHeight,
   * But if min-number-of-lines and maxNumberOfLines is provided,
   * this styles should be applied
   */
  return {
    minHeight: `calc(var(--typography-line-height, ${DEFAULT_LINE_HEIGHT})* var(--min-number-of-lines) + ${totalVerticalPadding}px) !important`,
    ...(maxNumberOfLines && {
      maxHeight: `calc(var(--typography-line-height, ${DEFAULT_LINE_HEIGHT}) * var(--max-number-of-lines) + ${totalVerticalPadding}px) !important`,
      overflowY: 'auto',
    }),
  };
};

export const STYLE_PROPERTIES_TO_COMPUTE = [
  'padding-top' as keyof CSSStyleDeclaration,
  'padding-bottom' as keyof CSSStyleDeclaration,
];
